import { useState, useEffect } from 'react';
import ConfirmModal from './ConfirmModal';
import HistoryTimeline from './HistoryTimeline';
import { APIClient } from "../helpers/api_helper";
import { CloseOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Col, Divider, Drawer, Form,
  Input, List, Row, Segmented, Space,
} from 'antd';
import { useWatch } from 'antd/es/form/Form';

const api = new APIClient();


const CompanyDrawer = (props) => {
  const [form] = Form.useForm();
  const [activeDrawerTab, setActiveDrawerTab] = useState('data');
  const [showDeletionConfirmModal, setShowDeletionConfirmModal] = useState(false);
  const [data, setData] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const typeValue = useWatch('type', form);

  useEffect(() => {
    setActiveDrawerTab('data');
    if (props.id) {
      try {
        api.get(`/api/companies/${props.id}/`, {})
          .then(resp => {
            const respData = {
              ...resp,
            };
            setData(respData);
            form.setFieldsValue(respData);
          });
      } catch (error) {
        console.error(error);
      }
    } else {
      form.resetFields();
      setData(null);
    }
  }, [props.isOpen, props.id]);


  const handleCancel = () => {
    form.resetFields();
    props.onCancel()
  }

  const handleConfirmedDelete = () => {
    api.delete(`/api/companies/${props.id}/`, {}, {successMsg: `Podjetje uspešno izbrisano ${props.id}: ${data.title}`})
      .then(resp => {
        props.onDone()
        form.resetFields();
        setShowDeletionConfirmModal(false);
      })
      .catch(error => {
        console.error(error)
      })
  };

  const onSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const payload = {
            ...values,
        };

        const isEdit = props.id !== null;
        const apiCall = isEdit
            ? api.update(`/api/companies/${props.id}/`, payload)
            : api.create('/api/companies/', payload, {successMsg: `Podjetje uspešno ustvarjeno ${payload.title}`});

        apiCall.then(resp => {
            props.onDone();
            form.resetFields();
          }).catch(err => console.error('API error:', err));
      })
      .catch((info) => {
        console.error('Validate Failed:', info);
      });
  };

  const isCreation = props.id == null;

  const handleSearch = () => {
    const title = form.getFieldValue('title');
    if (!title) return;

    setSearchLoading(true);
    api.get('/api/companies/external_search_companies/', { search: title })
      .then(resp => {
        setSearchResults(resp);
      })
      .catch(err => console.error('Search error:', err))
      .finally(() => setSearchLoading(false));
  };

  const handleSelectResult = (result) => {
    form.setFieldsValue(result);
    setSearchResults([]);
  };

  return (
    <>
      <Form layout="vertical" form={form}>
        <Drawer
          title={
            <Space>
              <span>{isCreation ? "Ustvari novo podjetje" : `Podjetje #${props.id}`}</span>
              {!isCreation && (
                <Segmented
                  size="small"
                  value={activeDrawerTab}
                  onChange={setActiveDrawerTab}
                  options={[
                    { label: 'Podatki', value: 'data' },
                    { label: 'Zgodovina', value: 'history' },
                  ]}
                />
              )}
            </Space>
          }
          width={720}
          onClose={handleCancel}
          open={props.open}
          extra={
            activeDrawerTab === 'data' && (
              <Space>
                <Button disabled={isCreation} onClick={() => setShowDeletionConfirmModal(true)} danger><DeleteOutlined /></Button>
                <Button onClick={handleCancel}><CloseOutlined /></Button>
                <Button disabled={false} onClick={onSubmit} type="primary">Shrani</Button>
              </Space>
            )
          }
        >
          {activeDrawerTab === 'data' && (
            <>
              <Row gutter={16}>
                <Col span={20}>
                  <Form.Item
                    name="title"
                    label='Naslov'
                    rules={[]}
                  >
                    <Input placeholder={'naslov...'} />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label=" ">
                    <Button
                      icon={<SearchOutlined />}
                      onClick={handleSearch}
                      loading={searchLoading}
                      block
                    />
                  </Form.Item>
                </Col>
              </Row>

              {searchResults.length > 0 && (
                <List
                  size="small"
                  bordered
                  dataSource={searchResults}
                  style={{ marginBottom: 16 }}
                  renderItem={(item) => (
                    <List.Item
                      onClick={() => handleSelectResult(item)}
                      style={{ cursor: 'pointer' }}
                    >
                      <List.Item.Meta
                        title={item.title}
                        description={`${item.address_street}, ${item.address_zip} ${item.address_city}`}
                      />
                    </List.Item>
                  )}
                />
              )}

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item name="full_title" label='Polni naziv'>
                    <Input placeholder={'polni naziv...'} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider/>

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item name="address_street" label='Ulica'>
                    <Input placeholder={'ulica...'} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="address_zip" label='Poštna številka'>
                    <Input placeholder={'poštna številka...'} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="address_city" label='Mesto'>
                    <Input placeholder={'mesto...'} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="country" label='Država'>
                    <Input placeholder={'država...'} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider/>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="ddv_id" label='Davčna številka'>
                    <Input placeholder={'davčna številka...'} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="reg_no" label='Matična številka'>
                    <Input placeholder={'matična številka...'} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider/>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="contact_person" label='Kontaktna oseba'>
                    <Input placeholder={'kontaktna oseba...'} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="phone" label='Telefon'>
                    <Input placeholder={'telefon...'} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="email" label='E-pošta'>
                    <Input placeholder={'e-pošta...'} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider/>
            </>
          )}

          {activeDrawerTab === 'history' && (
            <HistoryTimeline model="Company" objectId={props.id} />
          )}
        </Drawer>
      </Form>

      <ConfirmModal
        textComponent={<p>{`Ali ste prepričani, da želite izbrisati podjetje #${data?.id}?`}</p>}
        open={showDeletionConfirmModal}
        confirmBtnTitle='Izbriši'
        confirmBtnProps={{'danger': true}}
        onOk={() => handleConfirmedDelete()}
        onCancel={() => {
          setShowDeletionConfirmModal(false);
        }}
      />
    </>
  );
};

export default CompanyDrawer;
